import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';

/**
 * SignatureCanvas — touch + mouse drawing pad.
 * Exposes: isEmpty(), toDataURL(), clear() via ref.
 */
const SignatureCanvas = forwardRef(function SignatureCanvas({ width = 480, height = 160, style }, ref) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const [empty, setEmpty] = useState(true);

  useImperativeHandle(ref, () => ({
    isEmpty:    () => empty,
    toDataURL:  () => canvasRef.current?.toDataURL('image/png') ?? '',
    clear:      () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
      setEmpty(true);
    },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Set canvas internal resolution
    canvas.width  = canvas.offsetWidth  || width;
    canvas.height = canvas.offsetHeight || height;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#0a0a0a';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
  }, [width, height]);

  function getPos(e) {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top)  * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top)  * scaleY,
    };
  }

  function startDraw(e) {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function draw(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (empty) setEmpty(false);
  }

  function stopDraw(e) {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
    canvasRef.current.getContext('2d').beginPath();
  }

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'block',
        width: '100%',
        height,
        border: '2px solid #e0e0e0',
        borderRadius: 0,
        background: '#fafafa',
        cursor: 'crosshair',
        touchAction: 'none',
        ...style,
      }}
      onMouseDown={startDraw}
      onMouseMove={draw}
      onMouseUp={stopDraw}
      onMouseLeave={stopDraw}
      onTouchStart={startDraw}
      onTouchMove={draw}
      onTouchEnd={stopDraw}
    />
  );
});

export default SignatureCanvas;
